import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { customerApi } from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import { useInquiryCategories, uploadInquiryPhoto } from '../../components/inquiries/inquiryShared';

const MAX_PHOTOS = 8;

export default function SellPage() {
  const { settings } = useSettings();
  const categories = useInquiryCategories();
  const [form, setForm] = useState({ name: '', phone: '', email: '', item_description: '', item_category: '', inquiry_type: 'sell' });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(null); // access token of the new inquiry
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setError('');
    try {
      for (const file of files.slice(0, MAX_PHOTOS - images.length)) {
        const img = await uploadInquiryPhoto(file);
        setImages(prev => [...prev, img]);
      }
    } catch {
      setError('Photo upload failed. You can still submit without it.');
    }
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = (i) => setImages(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const r = await customerApi.post('/inquiries', { ...form, images });
      setSubmitted(r.data.access_token);
    } catch (e) { setError(e.response?.data?.error || 'Something went wrong. Please try again.'); }
    setSaving(false);
  };

  return (
    <div>
      <div className="page-hero">
        <h1>Sell or Pawn to Us</h1>
        <p>Get a fair offer on your items — fast, easy, and no obligation</p>
      </div>

      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'start' }}>

            {/* Form */}
            <div>
              <h2 className="section-title">Get a Quote</h2>
              <div className="gold-divider" />
              <p style={{ color: '#666', marginBottom: 16, lineHeight: 1.7 }}>
                Tell us about what you'd like to sell or pawn. We'll review your inquiry and get back to you with an offer. You can also walk in anytime during business hours.
              </p>
              <div className="alert alert-info" style={{ marginBottom: 20 }}>
                🪪 Pawn loans are only available to Louisiana residents, per state pawnbroking law. Selling items outright is open to everyone.
              </div>

              {submitted ? (
                <div className="alert alert-success" style={{ padding: '24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                  <h3 style={{ fontWeight: 700, marginBottom: 8 }}>Got it! We'll be in touch soon.</h3>
                  <p style={{ color: '#555', fontSize: 14 }}>We review every inquiry and typically respond within 24 hours. Your conversation page is where our reply will show up — you can add photos or details there any time.{form.email ? " We also emailed you the link." : ''}</p>
                  <Link to={`/inquiry/${submitted}`} className="btn btn-primary" style={{ marginTop: 16 }}>Open My Conversation →</Link>
                  <button className="btn btn-navy" style={{ marginTop: 16, marginLeft: 8 }} onClick={() => { setSubmitted(null); setImages([]); setForm({ name: '', phone: '', email: '', item_description: '', item_category: '', inquiry_type: 'sell' }); }}>Submit Another</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {error && <div className="alert alert-error">{error}</div>}

                  <div className="grid-2" style={{ gap: 16 }}>
                    <div className="form-group">
                      <label className="form-label">Your Name *</label>
                      <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="Full name" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(318) 000-0000" />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email <span style={{ fontWeight: 400, color: '#9a9a9a' }}>(phone or email required — we'll email you when we reply)</span></label>
                    <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="your@email.com" />
                  </div>

                  <div className="form-group">
                    <label className="form-label">I want to</label>
                    <div style={{ display: 'flex', gap: 12 }}>
                      {[['sell', 'Sell outright'], ['pawn', 'Pawn (loan)']].map(([val, label]) => (
                        <button key={val} type="button" onClick={() => set('inquiry_type', val)}
                          className={`btn ${form.inquiry_type === val ? 'btn-navy' : 'btn-outline'}`} style={{ flex: 1 }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-control" value={form.item_category} onChange={e => set('item_category', e.target.value)}>
                      <option value="">Select a category...</option>
                      {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Describe Your Item(s) *</label>
                    <textarea className="form-control" rows={5} value={form.item_description} onChange={e => set('item_description', e.target.value)} required
                      placeholder="Be as specific as possible — make, model, condition, any accessories, etc. The more detail you provide, the faster we can give you an accurate quote." />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Photos</label>
                    <p style={{ fontSize: 12, color: '#9a9a9a', marginTop: -2, marginBottom: 10 }}>Add a few photos of the item(s) to help us give you a faster, more accurate quote.</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {images.map((img, i) => (
                        <div key={img.public_id || i} style={{ position: 'relative', width: 76, height: 76 }}>
                          <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                          <button type="button" onClick={() => removePhoto(i)}
                            style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#c62828', color: '#fff', border: '2px solid #fff', fontSize: 11, lineHeight: 1, cursor: 'pointer' }}>✕</button>
                        </div>
                      ))}
                      {images.length < MAX_PHOTOS && (
                        <label style={{ width: 76, height: 76, border: '2px dashed #ddd', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#9a9a9a', fontSize: 12, textAlign: 'center', flexDirection: 'column' }}>
                          {uploading ? '...' : <>+<br />Add</>}
                          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhotoUpload} disabled={uploading} />
                        </label>
                      )}
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg" disabled={saving} style={{ width: '100%' }}>
                    {saving ? 'Submitting...' : 'Submit for Quote →'}
                  </button>

                  <button type="button" disabled title="Video consultation is coming soon"
                    style={{ width: '100%', marginTop: 10, padding: '12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#f8f7f2', color: '#9a9a9a', fontWeight: 700, fontSize: 14, cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    📹 Start Video Consultation <span className="badge badge-gray" style={{ fontSize: 10 }}>Coming Soon</span>
                  </button>
                </form>
              )}
            </div>

            {/* Info */}
            <div>
              <div className="card" style={{ padding: 28, marginBottom: 20 }}>
                <h3 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 16 }}>We Buy & Pawn</h3>
                {[
                  ['🎯', 'Guns & Firearms', 'All types — handguns, rifles, shotguns. FFL licensed.'],
                  ['💍', 'Jewelry & Watches', 'Gold, silver, diamonds, name-brand watches.'],
                  ['📱', 'Electronics', 'Phones, laptops, tablets, gaming consoles, TVs.'],
                  ['🔧', 'Tools', 'Power tools, hand tools, automotive equipment.'],
                  ['🎸', 'Musical Instruments', 'Guitars, amps, keyboards, DJ equipment.'],
                  ['⭐', 'Collectibles & Coins', 'Coins, sports cards, memorabilia, antiques.'],
                ].map(([icon, cat, desc]) => (
                  <div key={cat} style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2744', marginBottom: 2 }}>{cat}</div>
                      <div style={{ fontSize: 13, color: '#666' }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#1a2744', borderRadius: 14, padding: 24, color: '#fff' }}>
                <h3 style={{ fontWeight: 800, marginBottom: 12, color: '#c9a227' }}>How It Works</h3>
                {[
                  ['1', 'Submit your inquiry online or walk in'],
                  ['2', "We evaluate the item and give you a fair offer"],
                  ['3', 'Sell outright for cash or use it as loan collateral'],
                  ['4', 'Get paid same day — no waiting'],
                ].map(([n, step]) => (
                  <div key={n} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 26, height: 26, background: '#c9a227', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 900, fontSize: 13, color: '#1a2744' }}>{n}</div>
                    <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', alignSelf: 'center' }}>{step}</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: 16, paddingTop: 16, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                  📞 Or just call us: <a href={`tel:${(settings.store_phone || '').replace(/\D/g, '')}`} style={{ color: '#c9a227', fontWeight: 700 }}>{settings.store_phone}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
